'use server'

import { apiRequest, ApiResponse } from '../request'
import { downloadBlob } from '../../download-blob'

export type LedgerEntryType =
    | 'CREDIT_PAYMENT'
    | 'FEE'
    | 'TRANSFER_OUT'
    | 'REFUND'
    | 'CHARGEBACK'
    | 'SPLIT_IN'
    | 'SPLIT_OUT'
    | 'ADJUSTMENT'

export interface LedgerEntry {
    id: number
    organizationId: number
    providerEventId: string
    type: LedgerEntryType
    amount: number
    balanceAfter: number | null
    billingId: number | null
    billing?: { id: number; description: string | null; amount: number } | null
    transferId: number | null
    transfer?: { id: number; kind: string; amount: number } | null
    occurredAt: string
    description: string | null
    createdAt: string
    raw?: any
}

export interface LedgerEntryDetail extends LedgerEntry {
    billing?: {
        id: number
        description: string | null
        amount: number
        paidAmount: number | null
        status: string
        dueDate: string | null
        paidAt: string | null
        type: string
        paymentMethod: string | null
        providerPaymentId: string | null
    } | null
    transfer?: {
        id: number
        kind: string
        amount: number
        status: string
        description: string | null
        providerTransferId: string | null
        executedAt: string | null
        failReason: string | null
    } | null
}

const BASE = 'financial/ledger'

export interface LedgerFilters {
    type?: LedgerEntryType
    billingId?: number
    transferId?: number
    from?: string
    to?: string
    limit?: number
    unlinkedOnly?: boolean
    creditsOnly?: boolean
    search?: string
}

export async function getLedger(filters: LedgerFilters = {}): Promise<ApiResponse<LedgerEntry[]>> {
    const qs = new URLSearchParams()
    if (filters.type) qs.set('type', filters.type)
    if (filters.billingId) qs.set('billingId', String(filters.billingId))
    if (filters.transferId) qs.set('transferId', String(filters.transferId))
    if (filters.from) qs.set('from', filters.from)
    if (filters.to) qs.set('to', filters.to)
    if (filters.limit) qs.set('limit', String(filters.limit))
    if (filters.unlinkedOnly) qs.set('unlinkedOnly', 'true')
    if (filters.creditsOnly) qs.set('creditsOnly', 'true')
    if (filters.search) qs.set('search', filters.search)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return apiRequest<LedgerEntry[]>(`${BASE}${suffix}`, {})
}

export interface LedgerPaginatedFilters {
    type?: LedgerEntryType
    from?: string
    to?: string
    /** Valor mínimo em centavos (comparado pelo valor absoluto do movimento). */
    minAmount?: number
    /** Valor máximo em centavos (comparado pelo valor absoluto do movimento). */
    maxAmount?: number
    search?: string
    /** Só movimentações sem cobrança E sem transferência vinculada. */
    withoutLinks?: boolean
    page?: number
    limit?: number
}

export interface LedgerPage {
    items: LedgerEntry[]
    total: number
    page: number
    limit: number
    totalPages: number
}

/** Listagem do extrato com paginação numérica (tela /financial/statement). */
export async function getLedgerPaginated(
    filters: LedgerPaginatedFilters = {},
): Promise<ApiResponse<LedgerPage>> {
    const qs = new URLSearchParams()
    if (filters.type) qs.set('type', filters.type)
    if (filters.from) qs.set('from', filters.from)
    if (filters.to) qs.set('to', filters.to)
    if (filters.minAmount != null) qs.set('minAmount', String(filters.minAmount))
    if (filters.maxAmount != null) qs.set('maxAmount', String(filters.maxAmount))
    if (filters.search) qs.set('search', filters.search)
    if (filters.withoutLinks) qs.set('withoutLinks', 'true')
    if (filters.page) qs.set('page', String(filters.page))
    if (filters.limit) qs.set('limit', String(filters.limit))
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return apiRequest<LedgerPage>(`${BASE}/paginated${suffix}`, {})
}

/**
 * Conciliação manual: vincula uma movimentação do extrato a uma cobrança.
 * Se a cobrança ainda não baixou, o backend aplica a baixa (PAID + conciliado).
 */
export async function linkLedgerToBilling(
    ledgerEntryId: number,
    billingId: number,
): Promise<ApiResponse<{ linked: boolean; settled: boolean }>> {
    return apiRequest<{ linked: boolean; settled: boolean }>(
        `${BASE}/${ledgerEntryId}/link-billing`,
        { data: { billingId } },
        'post',
    )
}

/**
 * Cria o vínculo de um crédito do extrato que ficou sem cobrança, repetindo a
 * decisão do webhook: vincula à cobrança referenciada quando ela existe, senão
 * cria a cobrança avulsa (`created: true`).
 */
export async function createLedgerOrphanBilling(
    ledgerEntryId: number,
): Promise<ApiResponse<{ billingId: number; created: boolean; settled: boolean }>> {
    return apiRequest<{ billingId: number; created: boolean; settled: boolean }>(
        `${BASE}/${ledgerEntryId}/create-orphan-billing`,
        { data: {} },
        'post',
    )
}

/** Conciliação manual: desfaz o vínculo (e reverte a baixa se foi por este vínculo). */
export async function unlinkLedgerFromBilling(
    ledgerEntryId: number,
): Promise<ApiResponse<{ unlinked: boolean; reverted: boolean }>> {
    return apiRequest<{ unlinked: boolean; reverted: boolean }>(
        `${BASE}/${ledgerEntryId}/unlink-billing`,
        { data: {} },
        'post',
    )
}

export async function getLedgerEntry(id: number | string): Promise<ApiResponse<LedgerEntryDetail>> {
    return apiRequest<LedgerEntryDetail>(`${BASE}/${id}`, {})
}

export async function sendLedgerReceiptEmail(
    id: number | string,
    data: { email: string },
): Promise<ApiResponse<{ success: true }>> {
    return apiRequest<{ success: true }>(
        `${BASE}/${id}/send-receipt-email`,
        { data },
        'post',
    )
}

/**
 * Baixa o PDF do comprovante via blob (mantem header JWT do apiRequest).
 * Aciona download direto no navegador.
 */
export async function downloadLedgerReceiptPdf(id: number | string): Promise<void> {
    const { getClient } = await import('@/lib/api/client')
    const client = await getClient()
    const response = await client.get(`${BASE}/${id}/receipt.pdf`, { responseType: 'blob' })
    downloadBlob(new Blob([response.data], { type: 'application/pdf' }), `comprovante-${id}.pdf`)
}
